/**
 * Proctoring API client (Sprint 6 B5).
 *
 * Mirror backend endpoints (apps.proctoring):
 *   - POST /api/proctoring/sessions/<id>/consent/      — student accept consent
 *   - POST /api/proctoring/sessions/<id>/snapshots/    — upload webcam/screenshot (multipart)
 *   - POST /api/proctoring/sessions/<id>/violations/   — batch log violations
 *   - GET  /api/proctoring/sessions/<id>/              — detail (instructor only)
 */

import { useMutation, useQuery } from "@tanstack/react-query";
import { apiClient } from "./client";

// ─── Types ────────────────────────────────────────────────────────────────

export type ViolationType =
  | "tab_switch"
  | "fullscreen_exit"
  | "no_face"
  | "multiple_face"
  | "paste_attempt"
  | "copy_attempt"
  | "devtools_open"
  | "webcam_denied"
  | "consent_bypass";

export type ViolationSeverity = "low" | "medium" | "high";

export type SnapshotType = "webcam" | "screenshot";

export interface ViolationEvent {
  type: ViolationType;
  severity: ViolationSeverity;
  detail?: Record<string, unknown>;
  occurred_at: string; // ISO datetime
}

// ─── Hooks ────────────────────────────────────────────────────────────────

export interface ViolationLogRow {
  id: number;
  type: ViolationType;
  severity: ViolationSeverity;
  detail: Record<string, unknown>;
  occurred_at: string;
  logged_at: string;
}

export interface SnapshotRow {
  id: number;
  type: SnapshotType;
  sequence_no: number;
  image_url: string | null;
  captured_at: string;
}

export interface ProctorSessionRow {
  id: number;
  attempt: number;
  user: number;
  /** 2026-10-01 — hiển thị thay cho chỉ session id. */
  user_email?: string;
  user_name?: string;
  course_code?: string;
  course_name?: string;
  course_slug?: string;
  quiz_title?: string;
  status: "active" | "completed" | "terminated";
  consent_given_at: string | null;
  is_consent_given: boolean;
  webcam_enabled: boolean;
  user_agent: string;
  ip_address: string | null;
  started_at: string;
  completed_at: string | null;
  snapshots: SnapshotRow[];
  violations: ViolationLogRow[];
  violation_count: Record<ViolationSeverity, number>;
}

interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export function useProctorSessions(courseSlug?: string, hasViolations?: boolean) {
  return useQuery({
    queryKey: ["proctoring", "sessions", courseSlug ?? "", hasViolations ?? false],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (courseSlug) params.course = courseSlug;
      if (hasViolations) params.has_violations = "true";
      const resp = await apiClient.get<Paginated<ProctorSessionRow>>("/proctoring/sessions/", {
        params,
      });
      return resp.data.results;
    },
    staleTime: 30_000,
  });
}

export function useProctorSession(sessionId: number | undefined) {
  return useQuery({
    queryKey: ["proctoring", "session", sessionId],
    queryFn: async () => {
      if (!sessionId) throw new Error("sessionId required");
      const resp = await apiClient.get<ProctorSessionRow>(`/proctoring/sessions/${sessionId}/`);
      return resp.data;
    },
    enabled: !!sessionId,
  });
}

export function useGrantConsent(sessionId: number | undefined) {
  return useMutation({
    mutationFn: async () => {
      if (!sessionId) throw new Error("sessionId required");
      const resp = await apiClient.post(`/proctoring/sessions/${sessionId}/consent/`, {
        accepted: true,
      });
      return resp.data as { detail: string; consent_given_at: string };
    },
  });
}

export function useUploadSnapshot(sessionId: number | undefined) {
  return useMutation({
    mutationFn: async (payload: {
      blob: Blob;
      type: SnapshotType;
      sequenceNo: number;
      capturedAt: string;
    }) => {
      if (!sessionId) throw new Error("sessionId required");
      const form = new FormData();
      form.append("image", payload.blob, `${payload.type}_${payload.sequenceNo}.jpg`);
      form.append("type", payload.type);
      form.append("sequence_no", String(payload.sequenceNo));
      form.append("captured_at", payload.capturedAt);
      const resp = await apiClient.post(`/proctoring/sessions/${sessionId}/snapshots/`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return resp.data as { detail: string; snapshot_id: number; image_url: string };
    },
  });
}

export function useLogViolations(sessionId: number | undefined) {
  return useMutation({
    mutationFn: async (events: ViolationEvent[]) => {
      if (!sessionId) throw new Error("sessionId required");
      if (events.length === 0) return { logged: 0, received: 0 };
      const resp = await apiClient.post(`/proctoring/sessions/${sessionId}/violations/`, events);
      return resp.data as { detail: string; logged: number; received: number };
    },
  });
}
